import React, { FC } from "react";
import { observer } from "mobx-react";
import CheckoutFormSection from "./CheckoutFormSection";
import CheckoutIFrame from "./CheckoutIFrame";
import {
  CheckoutDetailSection,
  CheckoutPaymentWrapper,
  ProductDetailWrapper,
} from "../../../shared/styles";
import {
  CollapsiblePanel,
  CustomLabel,
  FlexContainer,
  Input,
  IOption,
  SingleSelect,
  CreatableSelect as SelectCreateable,
  CreatableSelect,
} from "@mindmemobile/mm-shared-global";
import { CheckOutStore } from "../../../shared/store";
import {
  CheckoutOptionsPageModel,
  CheckoutPaymentFormModel,
  CheckoutPaymentPageModel,
  MerchantAccount,
} from "../../../shared/models";
import {
  CheckOutPromoCodeSelectionType,
  PaymentMethodTypes,
  SellerTermsAndConditionsType,
  SurchargeType,
} from "../../../shared/enums";
import { ItemHeader } from "../../../shared/components";

type IProps = {
  checkoutStore: CheckOutStore;
};

const sellerTermsAndConditionsOptions: IOption[] = Object.keys(
  SellerTermsAndConditionsType
).map((key) => {
  return {
    label:
      SellerTermsAndConditionsType[
        key as keyof typeof SellerTermsAndConditionsType
      ],
    value: key,
  };
});

const surchargeTypes: IOption[] = Object.keys(SurchargeType).map((key) => {
  return {
    label: SurchargeType[key as keyof typeof surchargeTypes],
    value: key,
  };
});

function filterPaymentMethodBasedOnSettings(
  type: keyof typeof PaymentMethodTypes,
  selectedMerchantAccount?: MerchantAccount
) {
  if (selectedMerchantAccount && selectedMerchantAccount.paymentSettings) {
    switch (PaymentMethodTypes[type]) {
      case PaymentMethodTypes.CreditDebitCard:
        return (
          selectedMerchantAccount.paymentSettings.cards?.isEnabled === true
        );
        break;
      case PaymentMethodTypes.Bank:
        return (
          selectedMerchantAccount.paymentSettings.bankAch?.isEnabled === true
        );
        break;
      case PaymentMethodTypes.ApplePay:
        return (
          selectedMerchantAccount.paymentSettings.applePay?.isEnabled === true
        );
        break;
      case PaymentMethodTypes.GooglePay:
        return (
          selectedMerchantAccount.paymentSettings.googlePay?.isEnabled === true
        );
        break;
      case PaymentMethodTypes.CheckWire:
        return (
          selectedMerchantAccount.paymentSettings.checkWire?.isEnabled === true
        );
        break;
    }
  }

  return false;
}

const CheckoutPayment: FC<IProps> = (props: IProps) => {
  let paymentMethods: IOption[] = [];

  Object.keys(PaymentMethodTypes).map((key) => {
    const selectedMerchantAccount = props.checkoutStore.merchantAccounts?.find(
      (x) =>
        x.merchantAccountId ===
        props.checkoutStore.detailPage.selectedMerchantAccount
          ?.merchantAccountId
    );
    const isAllowed = filterPaymentMethodBasedOnSettings(
      key as keyof typeof PaymentMethodTypes,
      selectedMerchantAccount
    );
    if (isAllowed) {
      paymentMethods.push({
        label: PaymentMethodTypes[key as keyof typeof PaymentMethodTypes],
        value: key,
      });
    }
  });
  const [action, setAction] = React.useState<
    keyof CheckoutPaymentFormModel | null
  >(null);
  const { paymentPageData, optionsPage } = props.checkoutStore;
  const { formData } = paymentPageData;

  const handleInputChange = <T extends keyof CheckoutPaymentPageModel>(
    value: CheckoutPaymentPageModel[T],
    field: T
  ) => {
    const clonedData = new CheckoutPaymentPageModel(paymentPageData);
    if (field in formData) {
      clonedData.formData = new CheckoutPaymentFormModel({
        ...formData,
        [field]: value,
      });
    } else {
      clonedData[field] = value;
    }
    props.checkoutStore.updatePaymentPage(
      new CheckoutPaymentPageModel(clonedData)
    );
  };

  const checkBoxInput = (
    key: keyof CheckoutPaymentPageModel,
    value: boolean,
    label: string,
    disabled: boolean
  ) => {
    return (
      <Input
        type="checkbox"
        value={value}
        onChange={(e) => handleInputChange(e.target.checked, key)}
        label={label}
        disabled={disabled || false}
      />
    );
  };

  const textInput = (
    field: keyof CheckoutPaymentPageModel,
    value: string,
    placeholder: string,
    error: boolean,
    errorMessage?: string
  ) => (
    <Input
      type="textarea"
      value={value}
      onChange={(e) => handleInputChange(e.target.value, field)}
      placeholder={placeholder}
      className={`styled-input merchant-inputs `}
      error={error}
      errorMessage={errorMessage}
    />
  );

  const singleSelect = (
    field: keyof CheckoutPaymentPageModel,
    selectedKey: string,
    className: string,
    options: IOption[],
    error: boolean,
    errorMessage: string
  ): React.ReactNode => {
    return (
      <SingleSelect
        selectedKey={selectedKey}
        hasError={error}
        searchable={false}
        nameKeys={["label"]}
        valueKeys={["value"]}
        options={[options || []]}
        errorMessage={errorMessage}
        onChange={(option: any) => handleInputChange(option.value, field)}
        className={`dpdn-no-separator ${className}`}
      />
    );
  };

  const renderFormSection = () => {
    return (
      <CollapsiblePanel title="Form" className="checkout-labels">
        <CheckoutFormSection
          formData={props.checkoutStore.paymentPageData.formData}
          onChange={(
            field: keyof CheckoutPaymentPageModel,
            value: CheckoutPaymentFormModel
          ) => handleInputChange(value, field)}
          sendAction={(action: keyof CheckoutPaymentFormModel) =>
            setAction(action)
          }
        />
      </CollapsiblePanel>
    );
  };

  const renderTermsSection = () => {
    return (
      <CollapsiblePanel title="Terms" className="checkout-labels">
        <FlexContainer fxLayout="column" spacing={15}>
          {/* {checkBoxInput(
            "isUpdatesOffers",
            paymentPageData.isUpdatesOffers,
            "Opt-in for Updates / Offers",
            false
          )} */}
          {/* {checkBoxInput(
            "isAlternateCurrencies",
            paymentPageData.isAlternateCurrencies,
            "Require acceptance of terms & conditions  (update in Settings)",
            false
          )} */}
          <FlexContainer
            alignItems="center"
            spacing={15}
            padding="0px"
          >
            <CustomLabel className="seller-labels">
              Seller Terms & Conditions
            </CustomLabel>
            {singleSelect(
              "sellerTermsAndConditions",
              paymentPageData.sellerTermsAndConditions,
              "seller-terms-dropdown moneyback-chkout",
              sellerTermsAndConditionsOptions,
              false,
              ""
            )}
          </FlexContainer>
          <FlexContainer
            alignItems="center"
            spacing={15}
            padding="0px 0px 10px"
          >
            <CustomLabel className="seller-labels">
              Seller Return Policy
            </CustomLabel>
            {singleSelect(
              "sellerReturnPolicy",
              paymentPageData.sellerReturnPolicy,
              "seller-terms-dropdown moneyback-chkout",
              sellerTermsAndConditionsOptions,
              false,
              ""
            )}
          </FlexContainer>
          <CustomLabel className="seller-light-comments">
            Terms must exist for the selected Seller to be presented at
            Checkout. Update
            <br /> Terms for each Seller in Payment Settings.
          </CustomLabel>
          {/* {checkBoxInput(
            "allowTaxId",
            paymentPageData.allowTaxId,
            "Allow businesses to enter Tax ID's",
            false
          )} */}
          {/* {checkBoxInput(
            "isTermsConditions",
            paymentPageData.isTermsConditions,
            "Display prices in alternate currencies",
            false
          )} */}
          {/* {checkBoxInput(
            "isRefundPolicy",
            paymentPageData.isRefundPolicy,
            "Show Refund Policy",
            false
          )}
          {paymentPageData.isRefundPolicy && (
            <FlexContainer alignItems="center" spacing={15}>
              {singleSelect(
                "refundPolicyValue",
                paymentPageData.refundPolicyValue,
                "moneyback-chkout",
                [],
                false,
                ""
              )}
              {checkBoxInput(
                "requireAcceptance",
                paymentPageData.requireAcceptance,
                "Require Acceptance",
                false
              )}
            </FlexContainer>
          )} */}
        </FlexContainer>
      </CollapsiblePanel>
    );
  };

  const radioButton = (
    value: boolean,
    fieldName: keyof CheckoutPaymentPageModel,
    label: string,
    updateValue: keyof typeof CheckOutPromoCodeSelectionType
  ) => {
    return (
      <Input
        value={value}
        id={`rdb_${fieldName}`}
        type="radio"
        label={label}
        onChange={() => handleInputChange(updateValue, fieldName)}
        className={"chkbox-no-margin"}
      />
    );
  };

  const selectedPaymentMethods = () => {
    return (
      optionsPage.selectedPaymentMethods.map((b) => {
        return {
          label: PaymentMethodTypes[b as keyof typeof PaymentMethodTypes],
          value: b,
        } as IOption;
      }) || []
    );
  };

  const onDeleteSelectedItem = (newPaymentMethods: IOption[]) => {
    props.checkoutStore.updateOptionsPage(
      new CheckoutOptionsPageModel({
        ...optionsPage,
        selectedPaymentMethods: newPaymentMethods.map((item) => item.value),
      })
    );
  };

  //   const renderMarketingInputs = () => {
  //     return (
  //       <FlexContainer fxLayout="column" spacing={18}>
  //         {checkBoxInput(
  //           "showTimer",
  //           paymentPageData.showTimer,
  //           "Show Checkout timer",
  //           false
  //         )}
  //         {checkBoxInput(
  //           "isPromotionCodes",
  //           paymentPageData.isPromotionCodes,
  //           "Allow Promotion Codes",
  //           false
  //         )}
  //         {paymentPageData.isPromotionCodes && (
  //           <div>
  //             <FlexContainer
  //               alignItems="center"
  //               spacing={50}
  //               className={`marketing-radio-btns ${(paymentPageData.promoCodeSelectionType === CheckOutPromoCodeSelectionType.SelectCodes && "marketing-radio-spacing") || (paymentPageData.promoCodeSelectionType === CheckOutPromoCodeSelectionType.ApplyCode && "marketing-radio-spacing")}`}
  //             >
  //               {radioButton(
  //                 paymentPageData.promoCodeSelectionType ===
  //                   CheckOutPromoCodeSelectionType.AllActiveCodes,
  //                 "promoCodeSelectionType",
  //                 "All Active Codes",
  //                 CheckOutPromoCodeSelectionType.AllActiveCodes
  //               )}
  //               {radioButton(
  //                 paymentPageData.promoCodeSelectionType ===
  //                   CheckOutPromoCodeSelectionType.SelectCodes,
  //                 "promoCodeSelectionType",
  //                 "Select Codes",
  //                 CheckOutPromoCodeSelectionType.SelectCodes
  //               )}
  //               {radioButton(
  //                 paymentPageData.promoCodeSelectionType ===
  //                   CheckOutPromoCodeSelectionType.ApplyCode,
  //                 "promoCodeSelectionType",
  //                 "Apply Code",
  //                 CheckOutPromoCodeSelectionType.ApplyCode
  //               )}
  //             </FlexContainer>
  //             {paymentPageData.promoCodeSelectionType ===
  //               CheckOutPromoCodeSelectionType.SelectCodes && (
  //               <SelectCreateable
  //                 isMulti={true}
  //                 options={samplePromoCodes}
  //                 selectedOptions={selectedPromoCodes()}
  //                 // onChange={(newValue) => console.log(newValue)}
  //                 className="chkout-pay-select"
  //                 classPrefix="chkout-pay"
  //               />
  //             )}
  //             {paymentPageData.promoCodeSelectionType ===
  //               CheckOutPromoCodeSelectionType.ApplyCode &&
  //               singleSelect(
  //                 "applyCodeValue",
  //                 paymentPageData.applyCodeValue,
  //                 "moneyback-chkout",
  //                 [],
  //                 false,
  //                 ""
  //               )}
  //           </div>
  //         )}
  //         {checkBoxInput(
  //           "isTestimonial",
  //           paymentPageData.isTestimonial,
  //           "Show Testimonial Banner",
  //           false
  //         )}
  //         {paymentPageData.isTestimonial && (
  //           <SelectCreateable
  //             isMulti={true}
  //             options={samplePromoCodes}
  //             selectedOptions={selectedPromoCodes()}
  //             // onChange={(newValue) => console.log(newValue)}
  //             className="chkout-pay-select"
  //             classPrefix="chkout-pay"
  //           />
  //         )}
  //         {checkBoxInput(
  //           "isMoneyBack",
  //           paymentPageData.isMoneyBack,
  //           "Show Guarantee Banner",
  //           false
  //         )}
  //         {paymentPageData.isMoneyBack &&
  //           singleSelect(
  //             "moneyBackDuration",
  //             paymentPageData.moneyBackDuration,
  //             "moneyback-chkout",
  //             [],
  //             false,
  //             ""
  //           )}
  //       </FlexContainer>
  //     );
  //   };

  //   const renderMarketingSection = () => {
  //     return (
  //       <CollapsiblePanel
  //         title="Marketing"
  //         className="checkout-labels chkout-label-description"
  //       >
  //         <CustomLabel className="marketing-soft-text">
  //           Add available active options below from Payment Settings.
  //         </CustomLabel>
  //         {renderMarketingInputs()}
  //       </CollapsiblePanel>
  //     );
  //   };

  const renderPaymentMethodsContent = () => {
    return (
      <FlexContainer fxLayout="column" spacing={30}>
        <FlexContainer fxLayout="column">
          <CustomLabel margin="0px 0px 20px">
            Available Payment Methods
          </CustomLabel>
          <CreatableSelect
            isMulti={true}
            options={paymentMethods}
            selectedOptions={selectedPaymentMethods()}
            onChange={(newMethods) => onDeleteSelectedItem(newMethods)}
            className="payment-method-select"
            classPrefix="payment-method"
          />
        </FlexContainer>
        <FlexContainer
          fxLayout="column"
          spacing={0}
          className="surcharge-section"
        >
          <CustomLabel margin="10px 0px 15px">Surcharge</CustomLabel>
          {singleSelect(
            "surchargeType",
            paymentPageData.surchargeType,
            "seller-terms-dropdown seller-with-margin",
            surchargeTypes,
            false,
            ""
          )}
          {paymentPageData.surchargeType === "AskPermission" &&
            textInput(
              "surchargeText",
              paymentPageData.surchargeText,
              "Please enter surcharge amount",
              paymentPageData.hasError("surchargeText")
            )}
          <CustomLabel className="seller-light-comments">
            Surcharge applicable if it exists for the Payment Method selected at
            Checkout.
            <br /> Update Surcharges in Payment Settings.
          </CustomLabel>
        </FlexContainer>
      </FlexContainer>
    );
  };

  return (
    <CheckoutPaymentWrapper>
      <ProductDetailWrapper margin="0 0 25px">
        <FlexContainer>
          <CheckoutDetailSection>
            <div className="chckout-payment">
              <ItemHeader header="Payment" />
              {renderFormSection()}
              {/* {renderTermsSection()} */}
              <CollapsiblePanel
                title="Payment Methods"
                className="checkout-labels"
              >
                {renderPaymentMethodsContent()}
              </CollapsiblePanel>
              {/* {renderMarketingSection()} */}
            </div>
          </CheckoutDetailSection>
          <CheckoutIFrame checkoutStore={props.checkoutStore} action={action} />
        </FlexContainer>
      </ProductDetailWrapper>
    </CheckoutPaymentWrapper>
  );
};

export default observer(CheckoutPayment);
